import { create } from "zustand";

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  /** Stable key used to group the same transient error during one burst. */
  dedupeKey?: string;
  /** Number of identical errors received while this toast was visible. */
  count?: number;
}

interface ToastState {
  toasts: Toast[];
  addToast: (
    message: string,
    type: ToastType,
    duration?: number,
    dedupeKey?: string,
  ) => void;
  removeToast: (id: string) => void;
  removeByDedupeKey: (dedupeKey: string) => void;
  success: (message: string, dedupeKey?: string) => void;
  error: (message: string, dedupeKey?: string) => void;
  warning: (message: string, dedupeKey?: string) => void;
  info: (message: string, dedupeKey?: string) => void;
}

export const useToast = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (message, type, duration = 4000, dedupeKey = `${type}:${message}`) => {
    const existing = get().toasts.find((toast) => toast.dedupeKey === dedupeKey);
    if (existing) {
      // Several React Query requests can fail in the same render. Keep one
      // actionable toast and show the number of affected requests instead of
      // stacking identical messages over the page.
      set((state) => ({
        toasts: state.toasts.map((toast) =>
          toast.id === existing.id
            ? { ...toast, count: (toast.count ?? 1) + 1 }
            : toast,
        ),
      }));
      return;
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    set((state) => ({
      toasts: [...state.toasts, { id, message, type, dedupeKey, count: 1 }],
    }));
    if (duration > 0) {
      setTimeout(() => get().removeToast(id), duration);
    }
  },

  removeToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  removeByDedupeKey: (dedupeKey) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.dedupeKey !== dedupeKey) })),

  success: (message, dedupeKey) => get().addToast(message, "success", 4000, dedupeKey),
  error: (message, dedupeKey) => get().addToast(message, "error", 6000, dedupeKey),
  warning: (message, dedupeKey) => get().addToast(message, "warning", 5000, dedupeKey),
  info: (message, dedupeKey) => get().addToast(message, "info", 4000, dedupeKey),
}));
